import api from "./api.service";
import axios from "axios";

import { tokenService, CustomerData } from "./token.service";

// Tipo para el usuario de Google
export interface GoogleUser {
  uid: string;
  email: string;
  displayName: string;
  photoURL?: string;
  accessToken?: string;
}

// Tipos para la respuesta del servicio
export interface AuthResponse {
  user: GoogleUser | null;
  customerData: CustomerData | null;
  error: string | null;
  success: boolean;
  statusCode?: number;
  message?: string;
}

export interface UserProfile {
  email: string;
  nombre: string;
  apellido: string;
  phone: string;
  dni: string;
  dniType: string;
  photoURL?: string;
  createdAt: any;
  lastLogin: any;
  isActive: boolean;
}


class AuthService {
  // Helper para obtener datos del usuario de Google usando Google Identity Services (GSI)
  private async getGoogleUser(): Promise<GoogleUser> {
    return new Promise((resolve, reject) => {
      const google = (window as any).google;
      if (!google) {
        reject(
          new Error(
            "El SDK de Google no está cargado. Por favor, recarga la página."
          )
        );
        return;
      }

      try {
        const client = google.accounts.oauth2.initTokenClient({
          client_id:
            process.env.REACT_APP_GOOGLE_CLIENT_ID ||
            "975656292182-oqae5vlpvjnf911nr2ohev731gfek8vp.apps.googleusercontent.com",
          scope:
            "https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email",
          callback: async (tokenResponse: any) => {
            if (tokenResponse.error) {
              reject(
                new Error(
                  tokenResponse.error_description || tokenResponse.error
                )
              );
              return;
            }
            try {
              const response = await axios.get(
                "https://www.googleapis.com/oauth2/v3/userinfo",
                {
                  headers: {
                    Authorization: `Bearer ${tokenResponse.access_token}`,
                  },
                }
              );
              const info = response.data;
              resolve({
                uid: info.sub,
                email: info.email,
                displayName:
                  info.name ||
                  `${info.given_name || ""} ${info.family_name || ""}`.trim(),
                photoURL: info.picture,
                accessToken: tokenResponse.access_token,
              });
            } catch (err: any) {
              reject(
                new Error(
                  "Error al obtener la información de perfil de Google."
                )
              );
            }
          },
          error_callback: (err: any) => {
            reject(err);
          },
        });
        client.requestAccessToken();
      } catch (err: any) {
        reject(err);
      }
    });
  }

  // Helper para obtener el código de país ISO2 mediante la IP del cliente usando ipapi.co
  private async getCountryFromIP(): Promise<string> {
    try {
      const response = await axios.get("https://ipapi.co/json/");
      if (response.data && response.data.country_code) {
        console.log("País detectado por IP:", response.data.country_code);
        return response.data.country_code;
      }
      throw new Error("No se recibió el código de país en la respuesta.");
    } catch (error: any) {
      console.error("Error al detectar el país por IP:", error);
      throw new Error(
        "No se pudo detectar la ubicación por IP: " +
          (error.message || error)
      );
    }
  }

  // Login con Google (sin Firebase)
  async loginWithGoogle(countryCode?: string): Promise<AuthResponse> {
    try {
      const googleUser = await this.getGoogleUser();

      if (!googleUser.email || !googleUser.uid) {
        throw new Error(
          "No se pudo obtener la información del usuario de Google."
        );
      }

      // Resolver país: priorizar el parámetro countryCode, de lo contrario geolocalizar por IP
      const resolvedCountry = countryCode || (await this.getCountryFromIP());

      try {
        // 1. Intentar hacer login
        if (googleUser.accessToken) {
          await this.callCustomerLoginGoogleAPI(googleUser.email, googleUser.accessToken);
        } else {
          await this.callCustomerLoginAPI(googleUser.email, googleUser.uid);
        }
      } catch (loginError) {
        // 2. Si el login falla, asumimos que es porque el usuario no existe y lo registramos
        console.log(
          "Login fallido, probablemente el usuario no existe. Intentando registrar..."
        );

        const nameParts = googleUser.displayName?.split(" ") || [];
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";

        try {
          await this.callCustomerSigninGoogleAPI(
            googleUser.email,
            googleUser.uid, // Usando el 'sub' de Google como contraseña/ID
            firstName,
            lastName,
            resolvedCountry
          );

          // 3. Después de registrar, intentamos hacer login de nuevo
          if (googleUser.accessToken) {
            await this.callCustomerLoginGoogleAPI(googleUser.email, googleUser.accessToken);
          } else {
            await this.callCustomerLoginAPI(googleUser.email, googleUser.uid);
          }
        } catch (registerOrSecondLoginError) {
          // Si el registro o el segundo login fallan, lanzamos un error definitivo
          console.error(
            "Error durante el registro o el segundo login con Google:",
            registerOrSecondLoginError
          );
          throw new Error(
            "No se pudo registrar o iniciar sesión con la cuenta de Google."
          );
        }
      }

      // 4. Si llegamos aquí, el login (inicial o el segundo) fue exitoso. Obtenemos los datos.
      const td = tokenService.getCustomerDataToken();

      if (td?.customerData == null) {
        throw new Error(
          "No se encontraron datos del cliente después del login."
        );
      }

      const customerData = td.customerData;
      localStorage.setItem("isVerify", String(customerData.isVerify));

      return {
        user: googleUser,
        customerData: customerData,
        error: null,
        success: true,
      };
    } catch (error: any) {
      console.error("Error en el flujo de login con Google:", error);
      return {
        user: null,
        customerData: null,
        error: error.message || "Error al iniciar sesión con Google",
        success: false,
      };
    }
  }


  // Registrarse en el sistema propio con credenciales de Google
  async registerWithGoogle(countryCode?: string): Promise<AuthResponse> {
    // Se reutiliza la misma lógica de loginWithGoogle para unificar el flujo
    return this.loginWithGoogle(countryCode);
  }

  // Login con email y contraseña
  async loginWithEmail(email: string, password: string): Promise<AuthResponse> {
    try {
      // Llamar a la API externa de customer-login
      console.log("Attempting to call customer-login API...");
      const successLogin = await this.callCustomerLoginAPI(email, password);

      if (!successLogin) {
        return {
          user: null,
          customerData: null,
          error: null,
          success: false,
        };
      }
      const td = tokenService.getCustomerDataToken();

      if (td?.customerData == null) {
        return {
          user: null,
          customerData: null,
          error: null,
          success: false,
        };
      }

      const customerData = td.customerData;
      localStorage.setItem("isVerify", String(customerData.isVerify));
      return {
        user: null,
        customerData: customerData,
        error: null,
        success: true,
      };
    } catch (error: any) {
      console.error("Error en login con email:", error);
      return {
        user: null,
        customerData: null,
        error:
          error.response?.data?.message ||
          error.response?.data?.errorDetail ||
          error.message ||
          "Error al iniciar sesión.",
        success: false,
        statusCode: error.response?.status,
      };
    }
  }

  // Llamada a la API de customer-login con axios
  private async callCustomerLoginAPI(
    email: string,
    password: string,
  ): Promise<boolean> {
    try {
      const payload = {
        email: email,
        password: password,
      };

      const response = await api.post(`/customer-login`, payload);
      const token = response.data.token;
      if (token) {
        localStorage.setItem("authToken", token);
        return true;
      } else {
        console.warn("Token no recibido en la respuesta de customer-login.");
        return false;
      }
    } catch (error) {
      console.error("Error inesperado al llamar a customer-login:", error);
      throw error;
    }
  }

  // Llamada a la API de customer-login-google con axios
  private async callCustomerLoginGoogleAPI(
    email: string,
    accessToken: string,
  ): Promise<boolean> {
    try {
      const payload = {
        email: email,
      };

      const response = await api.post(`/customer-login-google`, payload, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      const token = response.data.token;
      if (token) {
        localStorage.setItem("authToken", token);
        return true;
      } else {
        console.warn("Token no recibido en la respuesta de customer-login-google.");
        return false;
      }
    } catch (error) {
      console.error("Error inesperado al llamar a customer-login-google:", error);
      throw error;
    }
  }

  // Verificar si un cliente existe por su email
  async checkCustomerExist(
    email: string,
  ): Promise<{ 
    exist: boolean; 
    statusCode: number; 
    data?: { 
      Id: number; 
      FirstName: string; 
      LastName: string; 
      ISO2Country: string; 
    } | null;
  }> {
    try {
      const response = await api.post<{ 
        statusCode: number; 
        exist: boolean; 
        data?: { 
          Id: number; 
          FirstName: string; 
          LastName: string; 
          ISO2Country: string; 
        } | null;
      }>(
        `/customer-exist`,
        { email },
        { headers: { "X-Skip-Loading": true } },
      );
      return {
        exist: response.data.exist === true,
        statusCode: response.data.statusCode,
        data: response.data.data,
      };
    } catch (error: any) {
      console.error("Error al verificar existencia del cliente:", error);
      return {
        exist: false,
        statusCode: error.response?.status || 500,
        data: null,
      };
    }
  }

  // Registro con email y contraseña (solo API externa)
  async registerWithEmail(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    phone: string,
    dni: string,
    dniType: string,
    countryCode?: string,
    razonSocial?: string,
  ): Promise<AuthResponse> {
    try {
      // Llamar únicamente a la API externa
      await this.callCustomerSigninAPI(
        email,
        password,
        firstName,
        lastName,
        phone,
        dni,
        dniType,
        countryCode,
        razonSocial,
      );

      // Devolver éxito. No hay usuario de Firebase, por lo que el user es null.
      return {
        user: null,
        customerData: null,
        error: null,
        success: true,
      };
    } catch (error: any) {
      return {
        user: null,
        customerData: null,
        error:
          error.response.data.errorDetail ||
          "Error al registrar en el sistema externo.",
        success: false,
      };
    }
  }

  // Registro con email para Pickup (sin contraseña, se genera en el backend)
  async registerPickupWithEmail(
    email: string,
    firstName: string,
    lastName: string,
    phone: string,
    dni: string,
    dniType: string,
    password: string,
    countryCode?: string,
    razonSocial?: string,
  ): Promise<AuthResponse> {
    try {
      const response = await this.callCustomerPickupSigninAPI(
        email,
        firstName,
        lastName,
        phone,
        dni,
        dniType,
        password,
        countryCode,
        razonSocial,
      );

      return response;
    } catch (error: any) {
      return {
        user: null,
        customerData: null,
        error:
          error.response?.data?.errorDetail ||
          "Error al registrar en el sistema externo para pickup.",
        success: false,
      };
    }
  }

  // Llamada a la API de customer-signin con axios
  private async callCustomerSigninAPI(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    phone: string,
    dni: string,
    dniType: string,
    countryCode?: string,
    razonSocial?: string,
  ): Promise<void> {
    try {
      const payload = {
        firstName: firstName,
        lastName: lastName,
        email: email,
        password: password,
        phone: phone,
        kindOfDNI: dniType,
        dni: dni,
        Country: countryCode || "VE",
        razonSocial: razonSocial || null,
      };

      await api.post(`/customer-signin`, payload);
      console.log("API customer-signin llamada con éxito.");
    } catch (error) {
      console.error("Error inesperado al llamar a customer-signin:", error);
      // Lanzar el error para que el método que llama pueda manejarlo
      throw error;
    }
  }

  // Llamada a la API de customer-signin-google con axios
  private async callCustomerSigninGoogleAPI(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    countryCode?: string,
  ): Promise<void> {
    try {
      const payload = {
        firstName: firstName,
        lastName: lastName,
        email: email,
        password: password,
        Country: countryCode || "VE",
      };

      await api.post(`/customer-signin-google`, payload);
      console.log("API customer-signin-google llamada con éxito.");
    } catch (error) {
      console.error(
        "Error inesperado al llamar a customer-signin-google:",
        error,
      );
      // Lanzar el error para que el método que llama pueda manejarlo
      throw error;
    }
  }

  // Llamada a la API de customer-pickup-signin con axios
  private async callCustomerPickupSigninAPI(
    email: string,
    firstName: string,
    lastName: string,
    phone: string,
    dni: string,
    dniType: string,
    password: string,
    countryCode?: string,
    razonSocial?: string,
  ): Promise<AuthResponse> {
    try {
      const payload = {
        firstName: firstName,
        lastName: lastName,
        email: email,
        password: password,
        phone: phone,
        kindOfDNI: dniType,
        dni: dni,
        Country: countryCode || "VE",
        razonSocial: razonSocial || null,
      };

      const response = await api.post(`/customer-pickup-signin`, payload);
      const data = response.data;

      if (data.statusCode === 200) {
        const token = data.token;
        if (token) {
          localStorage.setItem("authToken", token);
          return {
            user: null,
            customerData: null,
            error: null,
            success: true,
            statusCode: 200,
            message: data.message,
          };
        }
      }

      if (data.statusCode === 202) {
        return {
          user: null,
          customerData: null,
          error: null,
          success: true, // Se considera éxito en términos de flujo (usuario ya existe)
          statusCode: 202,
          message: data.message,
        };
      }

      return {
        user: null,
        customerData: null,
        error: data.message || "Error desconocido en pickup-signin",
        success: false,
        statusCode: data.statusCode,
      };
    } catch (error) {
      console.error(
        "Error inesperado al llamar a customer-pickup-signin:",
        error,
      );
      throw error;
    }
  }

  // Verificar cuenta con token
  async verifyAccount(verificationCode: string): Promise<AuthResponse> {
    try {
      const response = await api.post(`/verify-new-customer`, {
        verificationCode,
      });

      if (response.data.statusCode === 200) {
        return { user: null, customerData: null, error: null, success: true };
      } else {
        return {
          user: null,
          customerData: null,
          error:
            response.data.response.data.errorDetail ||
            "Error de verificación desconocido",
          success: false,
        };
      }
    } catch (error: any) {
      return {
        user: null,
        customerData: null,
        error: error.response.data?.errorDetail || "Error de red o servidor",
        success: false,
      };
    }
  }

  // Reenviar correo de verificación
  async resendVerificationEmail(email: string): Promise<{ success: boolean; error: string | null }> {
    try {
      const response = await api.post(`/resend-verification-email`, { email });
      if (response.data.statusCode === 200) {
        return { success: true, error: null };
      } else {
        return {
          success: false,
          error: response.data.errorDetail || "Error al reenviar el correo de verificación",
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.errorDetail || "Error de red o servidor al reenviar el correo",
      };
    }
  }

  // Actualizar perfil del cliente
  async updateCustomerProfile(
    firstName: string,
    lastName: string,
    phone: string,
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      const token = tokenService.getToken();
      if (!token) {
        return { success: false, error: "No hay token de autenticación." };
      }

      const payload = {
        firstName,
        lastName,
        phone,
      };

      await api.post(`/customer-update`, payload, {
        headers: {
          Authorization: `${token}`,
        },
      });

      // Después de actualizar el perfil, refrescar el token
      const refreshSuccess = await this.refreshToken();
      if (!refreshSuccess) {
        console.warn(
          "No se pudo refrescar el token después de la actualización del perfil.",
        );
      }

      return { success: true, error: null };
    } catch (error: any) {
      console.error("Error al actualizar el perfil del cliente:", error);
      return {
        success: false,
        error:
          error.response?.data?.errorDetail || "Error al actualizar el perfil.",
      };
    }
  }

  // Completar perfil del cliente (incluyendo DNI y tipo de DNI)
  async completeCustomerProfile(
    firstName: string,
    lastName: string,
    phoneNumber: string,
    dniType: string,
    dni: string,
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      const token = tokenService.getToken();
      if (!token) {
        return { success: false, error: "No hay token de autenticación." };
      }

      const payload = {
        firstName,
        lastName,
        phone: phoneNumber, // Cambiado a 'phone'
        kindOfDNI: dniType,
        dni,
      };

      await api.post(`/customer-complete`, payload, {
        headers: {
          Authorization: `${token}`,
        },
      });

      // Después de completar el perfil, refrescar el token
      const refreshSuccess = await this.refreshToken();
      if (!refreshSuccess) {
        console.warn(
          "No se pudo refrescar el token después de completar el perfil.",
        );
      }

      return { success: true, error: null };
    } catch (error: any) {
      console.error("Error al completar el perfil del cliente:", error);
      return {
        success: false,
        error:
          error.response?.data?.errorDetail || "Error al completar el perfil.",
      };
    }
  }

  // Método para refrescar el token
  async updateCustomerPassword(
    oldPassword: string,
    newPassword: string,
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      const token = tokenService.getToken();
      if (!token) {
        return { success: false, error: "No hay token de autenticación." };
      }

      const payload = {
        oldPassword,
        newPassword,
      };

      await api.post(`/customer-update-password`, payload, {
        headers: {
          Authorization: `${token}`,
        },
      });

      return { success: true, error: null };
    } catch (error: any) {
      console.error("Error al actualizar la contraseña:", error);
      return {
        success: false,
        error:
          error.response?.data?.errorDetail ||
          "Error al actualizar la contraseña.",
      };
    }
  }

  async refreshToken(): Promise<boolean> {
    try {
      const token = tokenService.getToken();
      if (!token) {
        console.warn("No hay token para refrescar.");
        return false;
      }

      const refreshResponse = await api.post(`/refresh-customer-token`, null, {
        headers: {
          Authorization: `${token}`,
        },
      });

      const newToken = refreshResponse.data.token;
      if (newToken) {
        tokenService.setToken(newToken);
        return true;
      } else {
        console.warn("No se recibió un nuevo token de refresh-customer-token.");
        return false;
      }
    } catch (error) {
      console.error("Error al refrescar el token:", error);
      return false;
    }
  }

  // Cerrar sesión
  async logout(): Promise<boolean> {
    try {
      // Eliminar el token de la API externa
      localStorage.removeItem("authToken");
      localStorage.removeItem("isVerify");
      localStorage.removeItem("hasSeenUnverifiedModal");

      console.log("Sesión cerrada y token eliminado.");
      return true;
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
      return false;
    }
  }

  // Recuperar contraseña
  async resetPassword(email: string): Promise<AuthResponse> {
    try {
      return {
        user: null,
        customerData: null,
        error: "Servicio de recuperación de contraseña temporalmente no disponible.",
        success: false,
      };
    } catch (error: any) {
      return {
        user: null,
        customerData: null,
        error: "Ha ocurrido un error inesperado",
        success: false,
      };
    }
  }

  // Obtener perfil de usuario
  async getUserProfile(uid: string): Promise<UserProfile | null> {
    return null;
  }
}

// Exportar instancia única del servicio
export const authService = new AuthService();
